import { html, LitElement, nothing, svg, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { type ClassInfo, classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { repeat } from 'lit/directives/repeat.js';
import {
  FetchPriority,
  ImageDecoding,
  ImageLoading,
  LabelValue,
  type TileType,
} from '../../base/package/api/package/gaming/gaming-domain';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import { handleKeyboardTrigger } from '../../base/package/util/a11y';
import { dispatchCustomEvent } from '../../base/package/util/event';
import { rasterizeToFirstFrame } from '../../base/package/util/game-asset-url';
import { reduceMotionStore } from '../../base/package/util/reduce-motion';
import { paintTick } from '../../base/package/util/render';
import styles from './ui-game-tile.css?inline';
import tileStyles from './ui-game-tile-sizes.css?inline';
import '../ui-screen-reader-only/ui-screen-reader-only';
import '../ui-time-ago/ui-time-ago';

const CName = 'ui-game-tile';

// Shared IntersectionObserver for reduce-motion rasterization.
// Tiles register themselves; when visible, their image is rasterized
// to a static frame. One observer instance for all game tile elements.
const rasterizeObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        const tile = entry.target as UIGameTile;
        rasterizeObserver.unobserve(tile);
        tile._triggerRasterize();
      }
    }
  },
  { rootMargin: '200px' },
);

// Label icons — inlined because these symbols are used exclusively by ui-game-tile.
// Co-locating path data eliminates the sprite sheet dependency and ~1,500 shadow roots
// on a category page with 500 tiles.
const LABEL_ICONS: Readonly<Partial<Record<LabelValue, ReturnType<typeof svg>>>> = {
  [LabelValue.EXCLUSIVE]: svg`<svg class="icon" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M6 2.333 4.806 5.027a.8.8 0 0 1-1.134.367l-2.15-1.252 1.255 4.111h6.446l1.256-4.111-2.15 1.252a.8.8 0 0 1-1.135-.367zm-.731-1.31a.8.8 0 0 1 1.462 0l1.38 3.11 2.646-1.542c.629-.366 1.38.23 1.168.926l-1.641 5.37a.8.8 0 0 1-.765.566H2.48a.8.8 0 0 1-.765-.566l-1.64-5.37c-.213-.696.538-1.292 1.167-.926L3.89 4.132zM1.9 10.852a.6.6 0 0 1 .6-.6h7a.6.6 0 1 1 0 1.2h-7a.6.6 0 0 1-.6-.6" clip-rule="evenodd"/></svg>`,
  [LabelValue.NEW]: svg`<svg class="icon" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path stroke="currentColor" stroke-width="1.2" d="M4.735 4.03 6 .614l1.265 3.418c.12.326.378.584.704.704L11.387 6 7.969 7.265c-.326.12-.584.378-.704.704L6 11.387 4.735 7.969a1.2 1.2 0 0 0-.704-.704L.613 6l3.418-1.265c.326-.12.584-.378.704-.704Z"/></svg>`,
  [LabelValue.TOP10]: svg`<svg class="icon" viewBox="0 0 10 12" fill="none" aria-hidden="true"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2" d="M2.47 3.324C1.458 4.314.75 5.73.75 6.989c0 1.13.448 2.214 1.245 3.013a4.245 4.245 0 0 0 6.01 0A4.27 4.27 0 0 0 9.25 6.99C9.25 4.525 8 2.18 6 .75L3.988 5.305z"/></svg>`,
};

export interface GameTileLatestWin {
  multiplier: string;
  amount: string;
  time: string;
  locale?: 'DE' | 'EN';
}

export interface GameTileLabel {
  id: LabelValue;
  value: string;
}

type ActionCallback = (ev?: any) => void;

/**
 * @prop {adaptiveWidth} - If true, the tile will adapt its width to the parent container
 * @fires maintenanceMode - On click when the game is in maintenance
 */
@customElement(CName)
export class UIGameTile extends LitElement {
  static readonly styles = [unsafeCSS(tileStyles), unsafeCSS(styles)];

  @property() clickAction?: ActionCallback;
  @property() infoAction?: ActionCallback;
  @property() type: TileType;
  @property({ type: Array }) labels?: GameTileLabel[];
  @property({ type: Boolean }) adaptiveWidth = false;
  @property({ type: Boolean }) activeUser = false;
  @property({ type: Boolean }) showVideo = false;
  @property({ type: Boolean }) isMaintenanceMode = false;
  @property({ type: Number }) rank?: number;
  @property({ type: Object }) latestWin?: GameTileLatestWin;
  @property({ type: String }) gameId: string;
  @property({ type: Boolean }) incentiveStyle = false;
  @property({ type: Boolean }) isInfoIconShown = true;
  @property({ type: String }) slug: string;
  @property({ type: String }) imageSrc: string;
  @property({ type: String }) priority: FetchPriority = FetchPriority.LOW;
  @property({ type: String }) videoSrc?: string;

  @state() _isPlaying = false;
  @state() _isLoading = false;
  @state() _showImageOverlay = true;
  @state() _isMaintenanceModalShown = false;

  @query('video') _videoElement?: HTMLVideoElement;

  @state() private _staticImgSrc?: string;

  private _retries = 0;
  private _retryTimerId: number | undefined;

  // Pre-bound handlers — allocated once per instance, reused across renders.
  // Eliminates ~1,000 closure allocations per render cycle on 500-tile pages.
  private readonly _handleAnchorKeydown = (ev: KeyboardEvent) => {
    handleKeyboardTrigger(ev, () => this._handleClick(ev));
  };

  private readonly _handleInfoKeydown = (ev: KeyboardEvent) => {
    if (this.infoAction) {
      handleKeyboardTrigger(ev, () => this.infoAction!(ev));
    }
  };

  private readonly _handleMaintenanceClick = () => {
    dispatchCustomEvent(this, 'maintenanceMode');
  };

  public async playVideo() {
    if (!this._videoElement || this._isPlaying) {
      return;
    }

    this._isLoading = true;

    try {
      this._videoElement.muted = true; // Set video to mute to avoid autoplay issues in Firefox
      await this._videoElement.play();
      await paintTick();
      this._showImageOverlay = false;
      this._isPlaying = true;
    } catch {
      // Happens if pauseVideo called before buffering finishes
      // can be safely ignored.
    } finally {
      this._isLoading = false;
    }
  }

  public pauseVideo() {
    if (!this._videoElement || this._videoElement.paused) {
      return;
    }

    // Reset video src to stop buffer, then set it back if the video is loading
    if (this._isLoading) {
      this._videoElement.src = '';
      this._videoElement.src = this.videoSrc ?? '';
    }

    this._showImageOverlay = true;
    this._isPlaying = false;
    this._videoElement.load();
  }

  /** Called by the shared IntersectionObserver when this tile becomes visible. */
  public _triggerRasterize() {
    if (reduceMotionStore.value) {
      this._rasterizeImage();
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    if (reduceMotionStore.value) {
      this._observeForRasterize();
    }
    if (this.videoSrc && this.showVideo && !reduceMotionStore.value) {
      this._registerInVideoOrchestrator();
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    rasterizeObserver.unobserve(this);
    if (this._retryTimerId !== undefined) {
      clearTimeout(this._retryTimerId);
      this._retryTimerId = undefined;
    }
  }

  render() {
    const classes: ClassInfo = {
      [this.type]: true,
      adaptive: this.adaptiveWidth,
      firstTile: this.rank === 1,
      hasRank: this.rank !== undefined,
      container: true,
      'game-tile--maintenance': this.isMaintenanceMode && this.activeUser,
    };

    const shouldPlayVideo = this.videoSrc && this.showVideo && !reduceMotionStore.value;
    const renderContent = shouldPlayVideo ? this._renderVideo() : this._renderImage();

    return html`
      <div class=${classMap(classes)} data-rank=${ifDefined(this.rank)} data-testid="game-tile">
        ${this.rank !== undefined ? html`<ui-screen-reader-only>${window.$app.$t.get(I18n.a11y.gameTile.rank, { rank: String(this.rank) })}</ui-screen-reader-only>` : nothing}
        <a class="anchor" href=${window.$app.router.getPathNavigateToGameInfo(this.slug)} @click=${this._handleClick}
          @keydown=${this._handleAnchorKeydown}
        >
          ${renderContent}
        </a>
        ${this._renderLabels()} ${this._renderInfoIcon()}
      </div>
      ${this._renderTileFooter()}
    `;
  }

  private _renderInfoIcon() {
    return this.activeUser && this.isInfoIconShown
      ? html`
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 20 20"
            @click=${this.infoAction}
            @keydown=${this._handleInfoKeydown}
            tabindex="0"
            class="info"
            role="img"
            aria-label=${window.$app.$t.get(I18n.a11y.button.gameInfo, { game: this.title })}
          >
            <title>${window.$app.$t.get(I18n.a11y.button.gameInfo, { game: this.title })}</title>
            <circle cx="10" cy="10" r="9.2" stroke="currentColor" stroke-opacity="0.16" stroke-width="1.6" />
            <path
              fill="currentColor"
              d="M9.2 6a.2.2 0 0 0-.2.2v1.6c0 .11.09.2.2.2h1.6a.2.2 0 0 0 .2-.2v-1.6a.2.2 0 0 0-.2-.2h-1.6Zm0 3a.2.2 0 0 0-.2.2v4.6c0 .11.09.2.2.2h1.6a.2.2 0 0 0 .2-.2v-4.6a.2.2 0 0 0-.2-.2h-1.6Z"
            />
          </svg>
        `
      : nothing;
  }

  private _renderLabels() {
    if (!this.labels) {
      return nothing;
    }

    const shouldShowLabel = this.labels.length === 1;
    return this.labels?.length > 0
      ? html`<div class="labels">
          ${repeat(
            this.labels,
            (label) => label.id.toString(),
            (label) => html`
              <div class="label ${label.id.toLowerCase()}">
                ${this._renderLabelIcon(label.id)}${this._renderLabel(label.value, shouldShowLabel)}
              </div>
            `,
          )}
        </div>`
      : nothing;
  }

  private _renderLabelIcon(label: LabelValue) {
    return LABEL_ICONS[label] ?? nothing;
  }

  private _renderLabel(label: string, shouldShowLabel = true) {
    return shouldShowLabel ? label : nothing;
  }

  private _renderTileFooter() {
    if (!this.latestWin) {
      return nothing;
    }

    return html`
      <div class="footer">
        <span class="multiplier">
          <span>&times;</span>
          <label>${this.latestWin.multiplier}</label>
        </span>
        <span class="time">
          <ui-time-ago timestamp=${this.latestWin.time} locale=${window.$app.appSettings.localeFormat}></ui-time-ago>
        </span>
      </div>
      <div class="amount">${this.latestWin.amount}</div>
    `;
  }

  private _renderImage(showPlaceholder = true) {
    // Override fetch priority for prerender mode to improve banner performance (LCP).
    const fetchPriority = window.$app.appConfig.prerenderMode ? FetchPriority.AUTO : this.priority;
    const loading = fetchPriority === FetchPriority.HIGH ? ImageLoading.EAGER : ImageLoading.LAZY;
    const decoding = fetchPriority === FetchPriority.HIGH ? ImageDecoding.SYNC : ImageDecoding.ASYNC;
    const imgSrc = this._staticImgSrc ?? this.imageSrc;
    const hasImage = imgSrc && imgSrc !== '';
    const classes = classMap({
      image: true,
      'image--incentive': this.incentiveStyle,
    });

    if (hasImage) {
      return html`
        <img
          @error=${this._handleImgError}
          @load=${this._handleImgLoaded}
          alt=${window.$app.$t.get(I18n.a11y.image.coverArtFor, { name: this.title })}
          class=${classes}
          crossorigin="anonymous"
          decoding=${decoding}
          fetchpriority=${fetchPriority}
          loading=${loading}
          src=${imgSrc}
        />
        ${this._renderMaintenance()}
      `;
    }
    if (showPlaceholder) {
      return html`<div class="placeholder"><span class="title">${this.title}</span></div>`;
    }
    return nothing;
  }

  private _renderVideo() {
    const wrapperClass = { videoWrapper: true, transparent: !this._showImageOverlay };
    // wrapper div is needed because preload="none" prevents swiping
    // on iOS carousel, so the div is ontop to allow it, and it also prevents video flicker
    const classes = classMap({
      video: true,
      'video--incentive': this.incentiveStyle,
    });

    return html`
      <div class=${classMap(wrapperClass)}>
        <video
          poster=${this.imageSrc}
          @ended=${this._videoEnded}
          class=${classes}
          crossorigin="anonymous"
          muted
          playsinline
          disablePictureInPicture
          disableRemotePlayback
          loading="lazy"
          preload="none"
          src=${ifDefined(this.videoSrc)}
          aria-label=${window.$app.$t.get(I18n.a11y.video.gameTile, { name: this.title })}
        ></video>
        ${this._renderImage(false)}
        <p lang="${window.$app.appConfig.lang}">${window.$app.$t.get(I18n.a11y.video.gameTile, { name: this.title })}</p>
      </div>
    `;
  }

  private _renderMaintenance() {
    return this.isMaintenanceMode && this.activeUser
      ? html`
          <div class="maintenance" @click=${this._handleMaintenanceClick}>
            ${window.$app.$t.get(I18n.maintenance.gameTile)}
          </div>
        `
      : nothing;
  }

  private _videoEnded() {
    this._showImageOverlay = true;
    this._isPlaying = false;
    dispatchCustomEvent(this, 'video-ended', this);
  }

  private _registerInVideoOrchestrator() {
    dispatchCustomEvent(this, 'register-playable', this);
  }

  private _rasterizeImage() {
    if (!this.imageSrc) return;
    rasterizeToFirstFrame(this.imageSrc).then((result) => {
      if (!this.isConnected) return;
      // Only swap if rasterization produced a data URL (different from src)
      if (result !== this.imageSrc) {
        this._staticImgSrc = result;
      }
    });
  }

  private _observeForRasterize() {
    rasterizeObserver.observe(this);
  }

  private _handleImgError() {
    this._retries++;
    if (this._retries > 3) {
      window.$app.logger.warn('loading image failed', this.imageSrc);
      return;
    }
    window.$app.logger.log('img load error; retry…', this.imageSrc, `(attempt ${this._retries})`);
    const src = this.imageSrc;
    this.imageSrc = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='; // Valid empty image
    this._retryTimerId = window.setTimeout(() => {
      this._retryTimerId = undefined;
      window.$app.logger.log('retry…', src);
      this.imageSrc = src;
    }, 250);
  }

  /**
   * Fade in images when loaded
   * TODO: add styles and refactor img tag
   */
  private _handleImgLoaded(ev: Event & { target: Element }) {
    ev.target.classList.add('loaded');
  }

  private _handleClick(ev: Event) {
    ev.preventDefault();
    if (!this.clickAction || (this.activeUser && this.isMaintenanceMode)) return;
    this.clickAction(ev);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIGameTile;
  }
}
